import type { ReactNode } from "react";
import type { OperationalMetric } from "../api/types";
import { formatCount, formatMeasuredCount, isValidCount } from "../formatting/number";

/**
 * Shared plain-number guard (extracted from `OverviewScreen.tsx`'s own
 * private copy, user follow-up 2026-09-29 "acceso rápido a todas las
 * métricas" — moving `ExecutionStatusMatrix` into `TelemetryScreen.tsx`
 * meant a SECOND consumer needed the exact same guard `AttentionLedger`
 * already used; a duplicated copy could silently drift out of sync with
 * this one). Every DTO-sourced plain `number` (not an `OperationalMetric`,
 * which `MetricCell` above already guards) rendered directly must be
 * validated the same way — `AdminPriorityItem.count`/`CampaignStateCount.count`
 * are both plain numbers on the wire with no backend-side invariant
 * enforcing finiteness, and `Intl.NumberFormat` happily turns
 * `NaN`/`Infinity` into the misleading strings `"NaN"`/`"∞"`.
 */
export function renderValidatedCount(value: number, formatter: (value: number) => string): ReactNode {
  if (!isValidCount(value)) {
    return <span className="v2-status v2-status--critical">Dato inválido</span>;
  }
  return formatter(value);
}

/**
 * `MetricCell` (design.md §6 "Statuses and feedback": "explicitly models
 * available, unavailable, stale, and error — not nullable-number
 * guesswork"). `metric` carries the backend's own availability contract
 * (design.md §7 capability model); `queryState` is an optional overlay for
 * the surrounding read itself being stale/failed — independent of any
 * single metric's own availability, so a read failure never gets rendered
 * as a fabricated `0` or as the unrelated F2-01 "unavailable" copy.
 */
export function MetricCell({
  metric,
  queryState,
  hideMeasuredSuffix = false,
}: {
  metric: OperationalMetric | undefined;
  queryState?: "stale" | "error";
  // User follow-up 2026-09-18 ("para la tarjeta de error no mostrar
  // medido, solo el contador"): confirmed by the prototype's own mobile
  // branch (línea 1258-1268) — a metric card's value is the bare number
  // ("35", "1.240") on mobile, never "0 · medido". Defaults to false
  // (unchanged) everywhere else this component is used.
  hideMeasuredSuffix?: boolean;
}) {
  if (queryState === "error" || !metric) {
    return <span className="v2-status v2-status--critical">No se pudo medir</span>;
  }

  if (metric.availability === "unavailable") {
    // UX audit 2026-09-15, finding #8 MEDIUM: "F2-01" is an internal
    // phase/ticket code, meaningless to a campaign operator. Product-facing
    // copy in the visible text; the ticket code moves to `title` only, for
    // anyone who genuinely needs it (support/eng), never as the primary text.
    return (
      <span className="v2-status v2-status--unavailable" title="Referencia interna: F2-01">
        No disponible · próximamente
      </span>
    );
  }

  // Review fix (Jose Luis Arcila, PR #95): the backend's `OperationalMetric`
  // C# constructor enforces "available => numeric value (0 allowed), never
  // null" — but that invariant lives server-side only; the frontend TS type
  // (`value: number | null`) cannot itself prevent a version-skew or
  // malformed response from violating it on the wire. `metric.value ?? 0`
  // used to silently coerce this invalid state into a real measurement,
  // FALSELY claiming "0 · medido" happened. This is a distinct failure mode
  // from `queryState === "error"` above (that is a READ failure; this is a
  // structurally invalid response that DID arrive) — never fall through to
  // `formatMeasuredCount`.
  //
  // Review fix follow-up (Jose Luis Arcila, PR #95): `value === null` alone
  // is not enough — NaN/Infinity/-Infinity are not `null` and would still
  // reach `formatMeasuredCount`, which renders the misleading literal
  // strings "NaN"/"∞" as if they were real measurements. `isValidCount`
  // rejects null (fails its `typeof === "number"` check) AND NaN/Infinity
  // (fails `Number.isFinite`) in one guard.
  const value = metric.value;
  if (!isValidCount(value)) {
    return <span className="v2-status v2-status--critical">Dato inválido</span>;
  }

  const label = hideMeasuredSuffix ? formatCount(value) : formatMeasuredCount(value);
  // UX audit 2026-09-15, finding #7 MEDIUM: the "· medido" suffix (design.md
  // §5.1 item 2) had no explanation anywhere in the UI — an operator seeing
  // "0 · medido" next to a plain "0" elsewhere had no way to know the
  // distinction is real: a confirmed zero (measured) vs. an ambiguous one.
  const titleParts = [
    value === 0 ? "Cero confirmado por una lectura real, no un dato faltante" : undefined,
    queryState === "stale" ? "Dato de una lectura anterior" : undefined,
  ].filter((part): part is string => Boolean(part));

  // 02-componentes.md "MetricCell": "Cero medido: literal '0 · medido' en
  // --v2-color-ink-faint" — a real measured zero reads visually distinct
  // from an ordinary measured number, not just via its "· medido" suffix.
  const className = value === 0 ? "v2-u-tabular-nums v2-u-ink-faint" : "v2-u-tabular-nums";

  return (
    <span className={className} title={titleParts.length > 0 ? titleParts.join(" · ") : undefined}>
      {label}
    </span>
  );
}
